/**
 * A agenda do dia em linha do tempo vertical.
 *
 * Este é o desenho que substitui a folha de papel. Cada atendimento é um bloco
 * proporcional à duração, então o barbeiro bate o olho e enxerga o dia: onde
 * está apertado, onde tem buraco e onde ele aceitou encaixar alguém por cima.
 *
 * Uma lista de cards não faria isso — mostraria os mesmos dados sem mostrar o
 * formato do dia, que é justamente o que ele lê na agenda de papel.
 */

import { type GridSlotState, gridSlotStates } from '@barbearia/core';
import type { AppointmentView, DayAgendaView } from '@barbearia/shared';
import { shortServicesLabel } from '@barbearia/shared';
import { useEffect, useRef } from 'react';
import { minutesOfDay, parseHhMm, timeOf } from '../lib/format.ts';
import { useNow } from '../lib/use-now.ts';

/**
 * Pixels por minuto.
 *
 * A 2px/min o menor serviço da casa (15 minutos) já rende 30px — altura para
 * uma linha legível com horário, cliente e serviço. Abaixo disso o bloco vira
 * uma tarja sem informação, que é o problema que a linha do tempo existe para
 * resolver.
 */
const SCALE = 2;

/** Nenhum bloco fica menor que o alvo de toque confortável. */
const MIN_BLOCK_HEIGHT = 44;

/** A partir daqui cabem duas linhas sem apertar. */
const TWO_LINE_HEIGHT = 58;

/**
 * Espaço deixado acima da linha do "agora" ao abrir a agenda.
 *
 * Não é zero de propósito: o atendimento que está terminando fica meio visível
 * em cima da linha, e é ele que o barbeiro tem na cadeira. Colar a linha no topo
 * mostraria o futuro e esconderia o presente.
 */
const HEADROOM = 72;

const STATUS_STYLE: Record<string, string> = {
  REQUESTED: 'border-dashed border-warn bg-warn/10',
  CONFIRMED: 'border-line bg-surface-raised',
  IN_PROGRESS: 'border-primary bg-primary/15',
  COMPLETED: 'border-line bg-surface-sunken',
  NO_SHOW: 'border-danger/40 bg-danger/5',
  REJECTED: 'border-line bg-surface-sunken',
  CANCELED_BY_CLIENT: 'border-line bg-surface-sunken',
  CANCELED_BY_SHOP: 'border-line bg-surface-sunken',
};

/**
 * O que não aconteceu: o horário está livre, e o cartão só conta a história.
 * É o que a agenda deixa de desenhar quando a pessoa escolhe esconder — o
 * concluído fica, porque aquele horário foi de fato usado.
 */
export const INACTIVE = new Set(['NO_SHOW', 'REJECTED', 'CANCELED_BY_CLIENT', 'CANCELED_BY_SHOP']);

const FADED = new Set(['COMPLETED', ...INACTIVE]);

interface TimelineProps {
  agenda: DayAgendaView;
  /** Data de hoje na barbearia — só então a linha do "agora" faz sentido. */
  today: string;
  onSelect: (appointment: AppointmentView) => void;
  /** Toque num vão livre já abre o novo agendamento naquele horário. */
  onPickFreeTime?: (minutes: number) => void;
}

export function Timeline({ agenda, today, onSelect, onPickFreeTime }: TimelineProps) {
  const timezone = agenda.timezone;
  const now = useNow();

  // A janela visível vem da jornada, mas precisa esticar para caber o que foi
  // encaixado fora do expediente — senão o atendimento some da tela.
  const bounds = computeBounds(agenda, timezone);
  const totalMinutes = bounds.end - bounds.start;
  const height = totalMinutes * SCALE;

  const nowMinutes = agenda.date === today ? minutesOfDay(now, timezone) : null;
  const showNow = nowMinutes !== null && nowMinutes >= bounds.start && nowMinutes <= bounds.end;

  const nowRef = useRef<HTMLDivElement>(null);
  const positioned = useRef<string | null>(null);

  /**
   * Abrir a agenda cai no que está para acontecer, não nas 8h da manhã.
   *
   * Roda uma vez por dia aberto: recarregar a agenda (alguém pediu horário, o
   * barbeiro concluiu um atendimento) não pode arrastar a tela de volta enquanto
   * ele está olhando outra parte do dia. Trocar de dia e voltar reposiciona,
   * porque aí a intenção é outra.
   *
   * A distância sai da diferença entre os retângulos, e não de `offsetTop`:
   * `offsetTop` conta a partir do ancestral posicionado — que aqui é a própria
   * linha do tempo — e daria a distância desde o começo do expediente, jogando
   * a rolagem para o fim do dia.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: `showNow` é gatilho, não leitura — o corpo precisa rodar quando a linha aparece, e `nowMinutes` mudaria a cada minuto, rolando a tela debaixo da mão do barbeiro
  useEffect(() => {
    const marker = nowRef.current;

    // Dia sem linha do "agora" (ontem, semana que vem): não há para onde rolar,
    // e esquecer aqui é o que faz voltar para hoje reposicionar. Sem isto, quem
    // desse uma olhada em ontem e voltasse continuaria no começo do expediente.
    if (!marker) {
      positioned.current = null;
      return;
    }

    if (positioned.current === agenda.date) return;

    const scroller = scrollParentOf(marker);
    if (!scroller) return;

    const markerBox = marker.getBoundingClientRect();
    const scrollerBox = scroller.getBoundingClientRect();

    scroller.scrollTop += markerBox.top - scrollerBox.top - stickyTopOf(scroller) - HEADROOM;
    positioned.current = agenda.date;
  }, [agenda.date, showNow]);

  // Com a grade fixa, a régua marca os horários da casa: 09:40 e 10:20 é o que
  // o barbeiro procura, e as horas cheias cairiam no meio dos atendimentos.
  const gridMinutes = agenda.grid.times.map(parseHhMm);
  const fixedGrid = gridMinutes.length > 0;

  const hours: number[] = [];
  if (fixedGrid) {
    hours.push(...gridMinutes);
  } else {
    for (let minute = Math.ceil(bounds.start / 60) * 60; minute <= bounds.end; minute += 60) {
      hours.push(minute);
    }
  }

  // Vão livre só faz sentido daqui para a frente: ninguém marca no passado, e
  // num dia que já passou o "livre" seria só o buraco que a agenda teve.
  const earliest = agenda.date < today ? Number.POSITIVE_INFINITY : (nowMinutes ?? 0);
  const occupied = onPickFreeTime ? occupiedRanges(agenda, timezone) : [];

  // Com a grade fixa, o que se toca é o horário da casa, não o vão: oferecer
  // "25 min livres" às 09:25 seria convidar a marcar fora da grade que a
  // barbearia escolheu. Quem quiser encaixar fora dela tem o botão de baixo.
  const gridSlots = fixedGrid
    ? gridSlotStates(gridMinutes, agenda.grid.stepMinutes, occupied).filter(
        (slot): slot is GridSlotState & { freeFrom: number } =>
          slot.freeFrom !== null && slot.minute >= earliest,
      )
    : [];
  const gaps = onPickFreeTime && !fixedGrid ? findGaps(agenda, bounds, occupied, earliest) : [];

  return (
    <div className="relative" style={{ height }}>
      {/* Régua das horas */}
      {hours.map((minute) => (
        <div
          key={minute}
          className="pointer-events-none absolute right-3 left-0 flex items-center gap-2"
          style={{ top: (minute - bounds.start) * SCALE }}
        >
          <span
            className={`w-12 shrink-0 text-right text-[12px] tabular-nums ${
              fixedGrid ? 'font-bold text-ink' : 'text-ink-faint'
            }`}
          >
            {formatHhMm(minute)}
          </span>
          <div className="h-px w-full bg-line" />
        </div>
      ))}

      {/* Bloqueios: folga, feriado, compromisso */}
      {agenda.blocks.map((block) => {
        const start = Math.max(bounds.start, minutesOfDay(block.startsAt, timezone));
        const end = Math.min(bounds.end, minutesOfDay(block.endsAt, timezone));
        if (end <= start) return null;

        return (
          <div
            key={block.id}
            className="absolute right-3 left-14 rounded-lg border border-dashed border-line bg-surface-sunken/80"
            style={{ top: (start - bounds.start) * SCALE, height: (end - start) * SCALE }}
          >
            <span className="px-2 text-[12px] text-ink-faint">{block.reason ?? 'Bloqueado'}</span>
          </div>
        );
      })}

      {/* Vãos livres: tocar abre o novo agendamento já naquele horário */}
      {gaps.map((gap) => (
        <button
          key={gap.start}
          type="button"
          onClick={() => onPickFreeTime?.(gap.start)}
          className="absolute right-3 left-14 rounded-lg pt-1 text-center align-top text-[12px] text-ink-faint active:bg-surface-raised"
          style={{ top: (gap.start - bounds.start) * SCALE, height: (gap.end - gap.start) * SCALE }}
        >
          {/* O rótulo fica no topo do vão, não centralizado: centralizado ele
              cai justamente em cima do atendimento seguinte. */}
          <span className="block leading-none">
            {gap.end - gap.start >= 30 ? `${gap.end - gap.start} min livres` : ''}
          </span>
        </button>
      ))}

      {/* Horários da grade ainda vagos: tocar marca exatamente ali */}
      {gridSlots.map((slot) => {
        const end = Math.min(
          slot.minute + agenda.grid.stepMinutes,
          nextBusyStart(occupied, slot.freeFrom),
        );

        return (
          <button
            key={slot.minute}
            type="button"
            onClick={() => onPickFreeTime?.(slot.minute)}
            className="absolute right-3 left-14 flex items-start gap-2 rounded-xl border border-dashed border-primary/60 bg-[color-mix(in_srgb,var(--color-primary)_6%,var(--color-surface))] px-3 pt-1.5 text-left active:bg-[color-mix(in_srgb,var(--color-primary)_18%,var(--color-surface))]"
            style={{
              // Fundo opaco e 1px de folga de cada lado: a linha da régua não
              // atravessa o cartão, e dois horários vagos seguidos não viram um
              // bloco só.
              top: (slot.freeFrom - bounds.start) * SCALE + 1,
              // Nunca menor que o alvo de toque: o resto de um horário invadido
              // pode ter cinco minutos, e o atendimento de cima, que tem z maior,
              // cobre o que sobrar.
              height: Math.max((end - slot.freeFrom) * SCALE - 2, MIN_BLOCK_HEIGHT),
              zIndex: 5,
            }}
          >
            <span className="text-[15px] font-semibold tabular-nums text-primary">
              {formatHhMm(slot.minute)}
            </span>
            <span className="pt-0.5 text-[12px] text-ink-soft">
              {slot.freeFrom > slot.minute ? `livre às ${formatHhMm(slot.freeFrom)}` : 'livre'}
            </span>
          </button>
        );
      })}

      {/* Atendimentos */}
      {agenda.appointments.map((appointment) => {
        const start = minutesOfDay(appointment.startsAt, timezone);
        const blockHeight = Math.max(appointment.durationMinutes * SCALE, MIN_BLOCK_HEIGHT);
        const compact = blockHeight < TWO_LINE_HEIGHT;
        const span = `${timeOf(appointment.startsAt, timezone)}–${timeOf(appointment.endsAt, timezone)}`;

        return (
          <button
            key={appointment.id}
            type="button"
            onClick={() => onSelect(appointment)}
            className={`absolute right-3 left-14 overflow-hidden rounded-xl border-l-4 px-3 py-1.5 text-left ${
              STATUS_STYLE[appointment.status] ?? 'border-line bg-surface-raised'
            } ${FADED.has(appointment.status) ? 'opacity-55' : ''}`}
            style={{
              top: (start - bounds.start) * SCALE,
              height: blockHeight,
              borderLeftColor: appointment.service.color,
              // Encaixes se sobrepõem: o mais tarde fica por cima e recuado,
              // para os dois continuarem tocáveis. O cancelado fica por baixo
              // de qualquer ativo: quem foi marcado no horário que ele deixou
              // é o que importa tocar.
              zIndex: INACTIVE.has(appointment.status)
                ? 6
                : appointment.overlapMinutes > 0
                  ? 20
                  : 10,
              left: appointment.overlapMinutes > 0 ? 68 : 56,
            }}
          >
            <div className="flex items-baseline gap-2">
              {/* Início e fim sempre, mesmo no bloco mais curto: é a primeira
                  coisa que o barbeiro procura ao bater o olho na agenda. */}
              <span className="shrink-0 text-[13px] font-semibold tabular-nums text-ink">
                {span}
              </span>
              <span className="truncate text-[14px] font-medium text-ink">
                {appointment.client.name}
              </span>
              {appointment.overlapMinutes > 0 && (
                <span className="shrink-0 text-[11px] font-semibold text-warn">
                  ⚠ {appointment.overlapMinutes}min
                </span>
              )}
            </div>

            <div
              className={`flex items-center gap-1.5 text-[12px] text-ink-soft ${
                compact ? 'leading-tight' : 'mt-0.5'
              }`}
            >
              {/* O serviço também aparece sempre. Num bloco curto ele divide a
                  linha com o resto; num bloco alto ganha a linha de baixo. Com
                  vários, só o primeiro e a contagem: "Cabelo +1" cabe aqui,
                  "Cabelo, Barba e Sobrancelha" vira reticências. */}
              <span className="truncate">
                {shortServicesLabel(appointment.services.map((service) => service.name))}
              </span>
              <span className="shrink-0 tabular-nums">· {appointment.durationMinutes}min</span>
              {appointment.series && (
                <span className="shrink-0" title={appointment.series.label}>
                  · ↻
                </span>
              )}
              {appointment.status === 'REQUESTED' && (
                <span className="shrink-0 font-semibold text-warn">· aguardando</span>
              )}
              {appointment.status === 'IN_PROGRESS' && (
                <span className="shrink-0 font-semibold text-primary">· atendendo</span>
              )}
              {appointment.status === 'NO_SHOW' && (
                <span className="shrink-0 font-semibold text-danger">· faltou</span>
              )}
            </div>
          </button>
        );
      })}

      {/* Onde estamos agora */}
      {showNow && (
        <div
          ref={nowRef}
          className="pointer-events-none absolute right-3 left-0 z-30 flex items-center"
          style={{ top: (nowMinutes - bounds.start) * SCALE }}
        >
          <span className="w-12 shrink-0 rounded bg-primary px-1 text-center text-[11px] font-bold tabular-nums text-primary-ink">
            {timeOf(now, timezone)}
          </span>
          <div className="h-0.5 w-full bg-primary" />
        </div>
      )}
    </div>
  );
}

/**
 * O contêiner que realmente rola — que aqui é o `<main>` do app, não a linha do
 * tempo. Procurar em vez de receber por prop evita que a agenda precise saber
 * como a casca do app está montada hoje.
 */
function scrollParentOf(element: HTMLElement): HTMLElement | null {
  let current = element.parentElement;

  while (current) {
    const overflow = getComputedStyle(current).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') return current;
    current = current.parentElement;
  }

  return null;
}

/**
 * Altura do cabeçalho que fica grudado no topo enquanto o resto rola.
 *
 * Sem descontar isto, a conta acerta o topo do contêiner — e o contêiner começa
 * *atrás* do cabeçalho. Foi assim que a linha do "agora" nasceu: rolava para o
 * lugar certo e parava escondida, com o horário pela metade.
 */
function stickyTopOf(scroller: HTMLElement): number {
  const header = scroller.querySelector('header');
  if (!(header instanceof HTMLElement)) return 0;

  return getComputedStyle(header).position === 'sticky' ? header.getBoundingClientRect().height : 0;
}

interface Bounds {
  start: number;
  end: number;
}

function computeBounds(agenda: DayAgendaView, timezone: string): Bounds {
  let start = Number.POSITIVE_INFINITY;
  let end = Number.NEGATIVE_INFINITY;

  for (const range of agenda.workingRanges) {
    start = Math.min(start, parseHhMm(range.start));
    end = Math.max(end, parseHhMm(range.end));
  }

  for (const appointment of agenda.appointments) {
    start = Math.min(start, minutesOfDay(appointment.startsAt, timezone));
    end = Math.max(end, minutesOfDay(appointment.startsAt, timezone) + appointment.durationMinutes);
  }

  // Dia de folga, sem nada marcado: mostra um expediente plausível em vez de
  // uma tela vazia sem referência nenhuma.
  if (!Number.isFinite(start) || !Number.isFinite(end)) return { start: 8 * 60, end: 20 * 60 };

  return {
    start: Math.max(0, Math.floor(start / 60) * 60),
    end: Math.min(24 * 60, Math.ceil(end / 60) * 60),
  };
}

function formatHhMm(minute: number): string {
  return `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
}

/**
 * O que ocupa a agenda, em minutos do dia. Só conta o que ainda está de pé: um
 * horário cancelado volta a ser vago, um concluído não.
 */
function occupiedRanges(agenda: DayAgendaView, timezone: string): Bounds[] {
  return agenda.appointments
    .filter((appointment) => !FADED.has(appointment.status))
    .map((appointment) => ({
      start: minutesOfDay(appointment.startsAt, timezone),
      end: minutesOfDay(appointment.startsAt, timezone) + appointment.durationMinutes,
    }))
    .concat(
      agenda.blocks.map((block) => ({
        start: minutesOfDay(block.startsAt, timezone),
        end: minutesOfDay(block.endsAt, timezone),
      })),
    )
    .sort((a, b) => a.start - b.start);
}

/** Onde começa o próximo atendimento depois de `minute` — o teto do vago. */
function nextBusyStart(occupied: readonly Bounds[], minute: number): number {
  const next = occupied.find((range) => range.start > minute);
  return next ? next.start : Number.POSITIVE_INFINITY;
}

/**
 * Espaços realmente livres dentro do expediente, entre um atendimento e outro.
 *
 * `earliest` corta o que já passou — oferecer "40 min livres" às 9h da manhã
 * quando já são 15h só atrapalharia.
 */
function findGaps(
  agenda: DayAgendaView,
  bounds: Bounds,
  busy: readonly Bounds[],
  earliest = 0,
): Bounds[] {
  const ranges =
    agenda.workingRanges.length > 0
      ? agenda.workingRanges.map((range) => ({
          start: parseHhMm(range.start),
          end: parseHhMm(range.end),
        }))
      : [bounds];

  const gaps: Bounds[] = [];

  for (const range of ranges) {
    let cursor = Math.max(range.start, earliest);
    if (cursor >= range.end) continue;

    for (const slot of busy) {
      if (slot.end <= cursor || slot.start >= range.end) continue;
      if (slot.start > cursor) gaps.push({ start: cursor, end: Math.min(slot.start, range.end) });
      cursor = Math.max(cursor, slot.end);
    }

    if (cursor < range.end) gaps.push({ start: cursor, end: range.end });
  }

  // Menos de 10 minutos não é buraco, é arredondamento.
  return gaps.filter((gap) => gap.end - gap.start >= 10);
}
